import React, { useEffect, useState } from "react";

import { FileText, FolderOpen } from "lucide-react";

import { ComplianceService } from "@/services/complianceService";
import Skeleton from "@/components/shared/Skeleton";

/**
 * A checklist's section → document tree, read only, rendered inline.
 *
 * For the surfaces that need to SHOW a checklist rather than open it: the
 * Compliance tab on a job, where the question is "what will we ask this
 * candidate for" and the answer belongs on the page, not behind a button.
 *
 * **Why not `ChecklistBuilderModal` with `readOnly`.** That component is the
 * editor, and `readOnly` turns it into the Checklist Details *modal* — it owns
 * a dialog, a two-pane layout, a selected-document pane and the draft state
 * behind all three. Inside a tab that is a modal that cannot open. This renders
 * the same server payload (`getChecklist`) as a flat list, so the two cannot
 * disagree about what a checklist contains; the builder stays the one place a
 * checklist is edited.
 *
 * Weight is shown per document because it is the thing that decides how much
 * each one moves the candidate's approval percentage — a recruiter looking at a
 * job's checklist is entitled to see which documents actually carry the run.
 */
const ChecklistReadOnlyView = ({ checklistId, className = "" }) => {
  const [sections, setSections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!checklistId) return undefined;
    let active = true;
    setLoading(true);
    ComplianceService.getChecklist(checklistId)
      .then((res) => {
        if (!active) return;
        setSections(res?.checklist?.sections ?? []);
        setFailed(false);
      })
      .catch((e) => {
        console.error(e);
        if (!active) return;
        setSections(null);
        setFailed(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [checklistId]);

  if (loading) {
    return (
      <div className={`flex flex-col gap-2 ${className}`}>
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  // Three states kept apart: a failed read must not render as a checklist with
  // no documents, which would tell a recruiter they are asking a candidate for
  // nothing.
  if (failed) {
    return <p className={`text-[12.5px] text-text-primary/60 ${className}`}>Could not load this checklist. Try again shortly.</p>;
  }

  if (!sections?.length) {
    return <p className={`text-[12.5px] text-text-primary/60 ${className}`}>This checklist has no sections yet.</p>;
  }

  return (
    <div className={`flex flex-col gap-2.5 ${className}`}>
      {sections.map((section) => (
        <div key={section.id} className="rounded-xl border border-border-primary bg-background-secondary p-3">
          <div className="flex items-center gap-2">
            <FolderOpen size={14} className="shrink-0 text-text-primary/70" aria-hidden="true" />
            <p className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-text-primary">{section.name}</p>
            <span className="shrink-0 text-[11px] text-text-secondary">
              {(section.subsections ?? []).length} {(section.subsections ?? []).length === 1 ? "document" : "documents"}
            </span>
          </div>

          {(section.subsections ?? []).length === 0 ? (
            <p className="mt-2 text-[12px] text-text-primary/50">No documents in this section.</p>
          ) : (
            <ul className="mt-2 flex flex-col divide-y divide-border-primary/60">
              {(section.subsections ?? []).map((sub) => (
                <li key={sub.id} className="flex items-center gap-2 py-2">
                  <FileText size={13} className="shrink-0 text-text-primary/50" aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[12.5px] text-text-primary">
                      {sub.name}
                      {sub.isRequired !== false && (
                        <span className="ml-0.5 text-[12px] font-bold text-tone-rose" title="Required document">
                          *
                        </span>
                      )}
                    </p>
                    {(sub.requiredFields ?? []).length > 0 && (
                      <p className="mt-0.5 truncate text-[11px] text-text-secondary" title={(sub.requiredFields ?? []).map((f) => f.fieldName).join(", ")}>
                        {(sub.requiredFields ?? []).map((f) => f.fieldName).join(" · ")}
                      </p>
                    )}
                  </div>
                  <span className="shrink-0 rounded border border-border-primary/40 bg-background-primary/60 px-1.5 py-0.5 text-[11px] font-semibold text-text-primary/70">
                    {Number(sub.weightPercentage) || 0}%
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
};

export default ChecklistReadOnlyView;
